import React, { useState, useEffect } from 'react';
import { Box, Tabs } from '@mantine/core';
import { IconTable, IconSchema, IconHistory, IconCode, IconUser } from '@tabler/icons-react';
import { GenericSearch } from './GenericSearch';
import { Tier2SchemaTabView } from './Tier2SchemaTabView';
import { Tier2SchemaRawView } from './Tier2SchemaRawView';
import { JournalEntriesView } from './JournalEntriesView';
import { isJournalEntry } from '../../utils/entityTypeDetection';
import { EntityActorSearchTab } from './EntityActorSearchTab';
import type { GenericSearchProps } from './GenericSearch';

const TAB_TABLE_VIEW = 'table';
const TAB_ACTOR_SEARCH = 'actor-search';
const TAB_SCHEMA = 'schema';
const TAB_JOURNAL = 'journal';

const SCHEMA_SUB_TAB_TABLE = 'ent2';
const SCHEMA_SUB_TAB_RAW = 'raw';

interface EntityTableWithTabsExtraProps {
    /** When true, hide the Schema and Journal top-level tabs (standard/simplified view). */
    hideAdminTabs?: boolean;
    /** When true, hide "Search In", Shift+Enter hint, and fullscreen/binding toggles in GenericSearch. */
    hideSearchExtras?: boolean;
    /** When true, hide the top pagination row in GenericSearch. */
    hideTopPagination?: boolean;
    /** When true, hide the Raw/$ tabs in GenericTable. */
    hideDebugTabs?: boolean;
    /** When true, hide actor search (disjointed entity types from tier1/tier2). */
    isDisjointed?: boolean;
}

export function EntityTableWithTabs<T>(props: Omit<GenericSearchProps<T>, 'tableVariant'> & EntityTableWithTabsExtraProps) {
    const { entityType, hideAdminTabs, hideSearchExtras, hideTopPagination, hideDebugTabs, isDisjointed = false, ...searchProps } = props;
    const [topTab, setTopTab] = useState<string | null>(TAB_TABLE_VIEW);
    const [schemaSubTab, setSchemaSubTab] = useState<string | null>(SCHEMA_SUB_TAB_TABLE);

    const isJournalEntity = isJournalEntry(entityType);
    const lowerEntityType = entityType?.toLowerCase();
    const isAllUsersPersonFacade = lowerEntityType === 'person';

    const showActorSearchTab = !hideAdminTabs && !!entityType?.trim() && !isJournalEntity && !isDisjointed && !isAllUsersPersonFacade;
    const showSchemaTab = !hideAdminTabs && !!entityType?.trim();
    const showJournalTab = !hideAdminTabs && !!entityType?.trim() && !isJournalEntity;
    const showTabBar = showSchemaTab || showJournalTab || showActorSearchTab;

    useEffect(() => {
        if (!showActorSearchTab && topTab === TAB_ACTOR_SEARCH) {
            setTopTab(TAB_TABLE_VIEW);
        }
    }, [showActorSearchTab, topTab]);

    return (
        <Box w="100%">
            {showTabBar && (
                <Tabs value={topTab} onChange={setTopTab} mb="md">
                    <Tabs.List>
                        <Tabs.Tab value={TAB_TABLE_VIEW} leftSection={<IconTable size={16} />}>
                            Table View
                        </Tabs.Tab>
                        {showActorSearchTab && (
                            <Tabs.Tab value={TAB_ACTOR_SEARCH} leftSection={<IconUser size={16} />}>
                                By Author
                            </Tabs.Tab>
                        )}
                        {showSchemaTab && (
                            <Tabs.Tab value={TAB_SCHEMA} leftSection={<IconSchema size={16} />}>
                                Schema
                            </Tabs.Tab>
                        )}
                        {showJournalTab && (
                            <Tabs.Tab value={TAB_JOURNAL} leftSection={<IconHistory size={16} />}>
                                Journal
                            </Tabs.Tab>
                        )}
                    </Tabs.List>
                </Tabs>
            )}

            {(topTab === TAB_TABLE_VIEW || !showTabBar) && (
                <GenericSearch {...searchProps} entityType={entityType} tableVariant="dataOnly" hideSearchExtras={hideSearchExtras} hideTopPagination={hideTopPagination} hideDebugTabs={hideDebugTabs} />
            )}
            {showActorSearchTab && topTab === TAB_ACTOR_SEARCH && entityType && (
                <EntityActorSearchTab entityType={entityType} />
            )}
            {showSchemaTab && topTab === TAB_SCHEMA && entityType && (
                <Box>
                    <Tabs value={schemaSubTab} onChange={setSchemaSubTab} mb="md">
                        <Tabs.List>
                            <Tabs.Tab value={SCHEMA_SUB_TAB_TABLE} leftSection={<IconTable size={16} />}>
                                Ent2
                            </Tabs.Tab>
                            <Tabs.Tab value={SCHEMA_SUB_TAB_RAW} leftSection={<IconCode size={16} />}>
                                Raw
                            </Tabs.Tab>
                        </Tabs.List>
                    </Tabs>
                    {schemaSubTab === SCHEMA_SUB_TAB_TABLE && (
                        <Tier2SchemaTabView entityType={entityType} />
                    )}
                    {schemaSubTab === SCHEMA_SUB_TAB_RAW && (
                        <Tier2SchemaRawView entityType={entityType} />
                    )}
                </Box>
            )}
            {showJournalTab && topTab === TAB_JOURNAL && entityType && (
                <JournalEntriesView entityType={entityType} />
            )}
        </Box>
    );
}
